import React from 'react';
import { Button, Col, FormGroup, FormLabel, Modal, ModalProps, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Form, Formik, useFormikContext } from 'formik';
import { object, string, date, number } from 'yup';
import { map } from 'lodash';
import { TrixEditor } from 'react-trix';

import { Genders, tGender } from '@models/User';
import { FormikControl, FormikSelect } from '@components/Base';
import { ActivationPeriods, InsurancePolicy } from '@models/InsurancePolicy';
import { InsurancePolicyUser } from '@models/InsurancePolicyUser';
import { noob } from '@utils/typescript';

import { InsurancePolicyUserCreatePayload } from './action';

export type InsurancePolicyUserFormValues = Omit<InsurancePolicyUserCreatePayload, 'insurancePolicyId'>;

type FormFormProps = Pick<ModalProps, 'onHide'> & Pick<InsurancePolicy, 'benefitTemplates'> & {
  isEdit: boolean;
};

const FormForm = ({ onHide, benefitTemplates, isEdit }: FormFormProps) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<InsurancePolicyUserFormValues>();

  return (
    <Form>
      <Modal.Header closeButton>
        <Modal.Title>{t('common:user')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup as={Row} className="mb-3" controlId="benefitTemplateId">
          <FormLabel column sm={4}>
            {t('common:benefitTemplate')}*
          </FormLabel>
          <Col sm>
            <FormikSelect name="benefitTemplateId" required >
              <option></option>
              {map(benefitTemplates, ({ id, name }) => (
                <option key={name} value={id}>
                  {name}
                </option>
              ))}
            </FormikSelect>
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="name">
          <FormLabel column sm={4}>
            {t('user.name')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="name" required disabled={isEdit} />
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="dob">
          <FormLabel column sm={4}>
            {t('user.dob')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="dob" type="date" required disabled={isEdit} />
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="gender">
          <FormLabel column sm={4}>
            {t('user.gender')}*
          </FormLabel>
          <Col sm>
            <FormikSelect name="gender" required disabled={isEdit} >
              <option></option>
              <option value={Genders.Female}>{tGender(Genders.Female)}</option>
              <option value={Genders.Male}>{tGender(Genders.Male)}</option>
            </FormikSelect>
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="activationDate">
          <FormLabel column sm={4}>
            {t('common:effectiveDate')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="activationDate" type="date" required />
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="activationPeriod">
          <FormLabel column sm={4}>
            {t('common:activationPeriod')}*
          </FormLabel>
          <Col sm>
            <FormikSelect name="activationPeriod" required >
              <option></option>
              {map(ActivationPeriods, (period) => (
                <option key={period} value={period}>
                  {period}
                </option>
              ))}
            </FormikSelect>
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="premium">
          <FormLabel column sm={4}>
            {t('common:premium')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="premium" type="number" required />
          </Col>
        </FormGroup>
        <FormGroup as={Row} className="mb-3" controlId="refundedPremium">
          <FormLabel column sm={4}>
            {t('common:refundedPremium')}*
          </FormLabel>
          <Col sm>
            <FormikControl name="refundedPremium" type="number" required />
          </Col>
        </FormGroup>

        <p>{t('common:fillDescriptionDetails')}*</p>
        <TrixEditor
          // key={key}
          className="trix-content"
          onChange={(html) => setFieldValue('descriptionHtml', html)}
          onEditorReady={noob}
          mergeTags={[]}
          value={values.descriptionHtml}
          placeholder={t('common:description')}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('confirmModal.cancel')}
        </Button>
        <Button type="submit" variant="primary">
          {t('common:save')}
        </Button>
      </Modal.Footer>
    </Form>
  );
}

type Props = ModalProps & Pick<InsurancePolicy, 'activationDate' | 'activationPeriod' | 'benefitTemplates'> & {
  onSubmit: (values: InsurancePolicyUserFormValues) => void;
  policyUser: InsurancePolicyUser | null;
}

const InsurancePolicyUserFormModal = ({ onSubmit, benefitTemplates, activationDate, activationPeriod, policyUser, ...props }: Props) => {
  const { t } = useTranslation();
  const isEdit = !!policyUser;

  const validationSchema = object({
    benefitTemplateId: number().required(t('validation:required')),
    name: string().required(t('validation:required')),
    dob: date().required(t('validation:required')),
    gender: string().required(t('validation:required')),
    activationDate: string().required(t('validation:required')),
    activationPeriod: string().required(t('validation:required')),
    premium: number().required(t('validation:required')),
    refundedPremium: number().required(t('validation:required')),
  });

  const initialValues: InsurancePolicyUserFormValues = {
    benefitTemplateId: policyUser?.benefitTemplateId || '',
    name: policyUser?.name || '',
    dob: policyUser?.dob || '',
    gender: policyUser?.gender || '',
    activationDate: policyUser?.activationDate || activationDate,
    activationPeriod: policyUser?.activationPeriod || activationPeriod,
    premium: policyUser?.premium || '',
    refundedPremium: policyUser?.refundedPremium || '',
    descriptionHtml: policyUser?.descriptionHtml || '',
  };

  return (
    <Modal {...props} size="xl">
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        <FormForm onHide={props.onHide} benefitTemplates={benefitTemplates} isEdit={isEdit} />
      </Formik>
    </Modal>
  )
};

export { InsurancePolicyUserFormModal };
